import { useCallback } from 'react';
import { DataProvider, useDataProvider } from 'react-admin';
import {
    useMutation,
    UseMutationOptions,
    UseMutationResult,
} from 'react-query';

import {
    GetChildNodesParams,
    GetChildNodesResult,
    TreeDataProvider,
    TreeRecord,
} from '../types';

/**
 * Internal hook used to lazy load a node children using the dataProvider.getChildNodes() method.
 *
 * @param {string} resource The resource name, e.g. 'posts'
 * @param {Params} params The getChildNodes parameters
 * @param {Object} options Options object to pass to the queryClient.
 * May include side effects to be executed upon success or failure, e.g. { onSuccess: () => { refresh(); } }
 *
 * @typedef Params
 * @prop params.parentId The parent node identifier
 * @prop params.meta Optional meta parameters

 * The data structure is a flat array of TreeRecords (i.e. Records with a children field containing an array of Identifiers).
 *
 * @example // sample return value
 * [
 *   { id: 1, title: 'foo1', children: [3, 4] },
 *   { id: 2, title: 'foo2', children: [] },
 *   { id: 3, title: 'foo3', children: [5] },
 *   { id: 4, title: 'foo4', children: [] },
 *   { id: 5, title: 'foo5', children: [] },
 * ]
 */
export const useGetChildNodesCallback = <
    RecordType extends TreeRecord = TreeRecord
>(
    resource: string,
    params?: Partial<GetChildNodesParams<RecordType>>,
    options?: UseMutationOptions<
        GetChildNodesResult<RecordType>['data'],
        Error,
        Partial<GetChildNodesParams<RecordType>>
    >
): UseGetChildNodesCallbackValue<RecordType> => {
    const dataProvider = useDataProvider<TreeDataProvider & DataProvider>();
    const mutation = useMutation<
        GetChildNodesResult<RecordType>['data'],
        unknown,
        GetChildNodesParams<RecordType>
    >(
        [resource, 'getChildNodes', params],
        variables =>
            dataProvider
                .getChildNodes<RecordType>(resource, {
                    ...params,
                    ...variables,
                })
                .then(({ data }) => data),
        {
            ...options,
        }
    );

    const callback = useCallback<UseGetChildNodesCallback<RecordType>>(
        parentId => {
            return mutation.mutateAsync({ ...params, parentId });
        },
        [mutation, params]
    );
    return callback;
};

export interface UseGetChildNodesCallbackParams {
    parentId?: string;
    meta?: any;
}

export type UseGetChildNodesCallback<
    RecordType extends TreeRecord = TreeRecord
> = (
    parentId: RecordType['id']
) => ReturnType<
    UseMutationResult<
        GetChildNodesResult<RecordType>['data'],
        unknown,
        GetChildNodesParams<RecordType>
    >['mutateAsync']
>;

export type UseGetChildNodesCallbackValue<
    RecordType extends TreeRecord = TreeRecord
> = UseGetChildNodesCallback<RecordType>;
